import { loadPageTranslations } from '@/lib/i18n-loaders';
import { syncWithLaravelLocale } from '@/lib/i18n';
import type { SharedData } from '@/types';
import { router, usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function useLocale() {
    const { i18n } = useTranslation();
    const page = usePage<SharedData>();
    const { locale, availableLocales } = page.props;

    useEffect(() => {
        if (!locale || i18n.language === locale) {
            return;
        }

        void loadPageTranslations(locale, page.component).then(() => {
            syncWithLaravelLocale(locale);
        });
    }, [locale, i18n.language, page.component]);

    const changeLocale = async (newLocale: string) => {
        if (!availableLocales.includes(newLocale) || newLocale === locale) {
            return;
        }

        const previousLocale = i18n.language;

        // Load the target bundles BEFORE navigating: Inertia caches page modules,
        // so resolve() (and its translation loading) is skipped when the component
        // name does not change. Switching afterwards would paint missing keys.
        try {
            await loadPageTranslations(newLocale, page.component);
            syncWithLaravelLocale(newLocale);
        } catch {
            // Loading failed (e.g. offline); proceed anyway — the effect above
            // retries once the server confirms the new locale.
        }

        // POST to LocaleController which saves locale to both session
        // and database (for authenticated users), then redirects to
        // the current page with the new locale prefix.
        router.post(route('locale.switch'), { locale: newLocale }, {
            preserveScroll: true,
            onError: () => syncWithLaravelLocale(previousLocale),
        });
    };

    return {
        currentLocale: locale,
        availableLocales,
        changeLocale,
    };
}
